/**
 * The one refresh clock of the console (item 77) and the pause that goes with it (item 6).
 *
 * Every data query is created with a key that starts with `c3`. This clock ticks once every
 * `intervalMs`, refetches the queries a screen is showing at that moment, and nothing else refreshes
 * data on its own. A screen never sets its own timer.
 *
 * Pausing freezes what the screens show: the queries keep reading on each tick, so the clock can say
 * how many of them now hold something different, but a screen is handed the data it had when the
 * pause began until the clock resumes. The "as of" time is the oldest moment the data on screen
 * stands for, so a line that says "as of 14:02:10" is true of every figure under it.
 *
 * It does not refresh while the tab is hidden, and refreshes once when the tab comes back if a tick
 * went by in the meantime.
 *
 * The Threat Feed is to receive updates as they happen (client notes 10/26, item 20). The backend has no channel to push
 * them, so the clock has a second, faster lane: every `liveIntervalMs` it reads again only the records the feed is built
 * from (`LIVE_RESOURCES`), and only while a screen is showing them. It is still the one clock: it pauses, hides and
 * resumes with the other, and a screen sets no timer of its own. A push channel would replace the lane, not the screens.
 */
import type { QueryClient } from '@tanstack/react-query';

export const REFRESH_INTERVAL_MS = 30_000;
/** How often the records of the Threat Feed are read again. */
export const LIVE_REFRESH_INTERVAL_MS = 5_000;
/** The resources the fast lane reads: the threats, their alerts and actions, and the plans that decide their state. */
export const LIVE_RESOURCES: readonly string[] = ['threatEvents', 'threatCandidates', 'threatElements', 'remediations'];
/** First element of every query key this layer creates. */
export const C3_QUERY_ROOT = 'c3';

export interface ClockState {
  paused: boolean;
  /** Queries on screen whose data has changed since the pause. Always 0 while running. */
  pendingCount: number;
  /** The oldest moment the data on screen stands for, in ms, or null before anything has loaded. */
  asOf: number | null;
  intervalMs: number;
  /** How often the Threat Feed's records are read again; 0 when there is no fast lane. */
  liveIntervalMs: number;
}

/** What a query showed when the pause began. */
export interface FrozenData {
  data: unknown;
  at: number;
}

export interface DataClock {
  subscribe(listener: () => void): () => void;
  getState(): ClockState;
  /** The data a query is held at while paused; undefined when running or when it began after the pause. */
  frozenFor(queryHash: string): FrozenData | undefined;
  pause(): void;
  resume(): void;
  /** Shows the latest data and reads again now. Resolves when the reads are done, failed or not. */
  refreshNow(): Promise<void>;
  start(): void;
  stop(): void;
}

export function createDataClock(
  queryClient: QueryClient,
  intervalMs: number = REFRESH_INTERVAL_MS,
  liveIntervalMs: number = 0
): DataClock {
  const listeners = new Set<() => void>();
  const frozen = new Map<string, FrozenData>();
  let paused = false;
  let missedTick = false;
  let state: ClockState = { paused: false, pendingCount: 0, asOf: null, intervalMs, liveIntervalMs };

  const activeQueries = () =>
    queryClient.getQueryCache().findAll({ queryKey: [C3_QUERY_ROOT], type: 'active' });

  const compute = (): ClockState => {
    let pendingCount = 0;
    let asOf: number | null = null;
    for (const query of activeQueries()) {
      const held = frozen.get(query.queryHash);
      if (held && query.state.data !== held.data) pendingCount += 1;
      const at = held ? held.at : query.state.dataUpdatedAt;
      if (at > 0) asOf = asOf == null ? at : Math.min(asOf, at);
    }
    return { paused, pendingCount, asOf, intervalMs, liveIntervalMs };
  };

  const update = () => {
    const next = compute();
    if (
      next.paused === state.paused &&
      next.pendingCount === state.pendingCount &&
      next.asOf === state.asOf
    ) {
      return;
    }
    state = next;
    listeners.forEach((listener) => listener());
  };

  const readAgain = (): Promise<void> =>
    queryClient.refetchQueries({ queryKey: [C3_QUERY_ROOT], type: 'active' });

  const readLive = (): Promise<void> =>
    queryClient.refetchQueries({
      queryKey: [C3_QUERY_ROOT],
      type: 'active',
      // The key is [root, source, organization, resource, ...].
      predicate: (query) => LIVE_RESOURCES.includes(String(query.queryKey[3])),
    });

  const tabHidden = () => typeof document !== 'undefined' && document.hidden;

  const tick = () => {
    if (tabHidden()) {
      missedTick = true;
      return;
    }
    void readAgain();
  };

  const tickLive = () => {
    if (tabHidden()) {
      missedTick = true;
      return;
    }
    void readLive();
  };

  const onVisibility = () => {
    if (tabHidden() || !missedTick) return;
    missedTick = false;
    void readAgain();
  };

  let timer: ReturnType<typeof setInterval> | null = null;
  let liveTimer: ReturnType<typeof setInterval> | null = null;
  let unsubscribeCache: (() => void) | null = null;

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getState: () => state,
    frozenFor: (queryHash) => (paused ? frozen.get(queryHash) : undefined),
    pause() {
      if (paused) return;
      for (const query of activeQueries()) {
        if (query.state.data !== undefined) {
          frozen.set(query.queryHash, { data: query.state.data, at: query.state.dataUpdatedAt });
        }
      }
      paused = true;
      update();
    },
    resume() {
      if (!paused) return;
      frozen.clear();
      paused = false;
      update();
    },
    refreshNow() {
      if (paused) {
        frozen.clear();
        paused = false;
        update();
      }
      return readAgain();
    },
    start() {
      if (timer) return;
      timer = setInterval(tick, intervalMs);
      if (liveIntervalMs > 0) liveTimer = setInterval(tickLive, liveIntervalMs);
      unsubscribeCache = queryClient.getQueryCache().subscribe(update);
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibility);
      }
      update();
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
      if (liveTimer) clearInterval(liveTimer);
      liveTimer = null;
      unsubscribeCache?.();
      unsubscribeCache = null;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    },
  };
}
